import { useState, useEffect, useMemo } from 'react';
import { Button, CoordioDropdown, FormField, Input, Textarea, useStaticDropdownSource } from '@coordio/ui';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { AGENT_DROPDOWN, agentToDropdownOption } from '../agents/agentDropdown';
import { agentService } from '../../services/agentService';
import type { AgentDefinitionDto } from '../../types/agent';

interface AttachAgentDialogProps {
  open: boolean;
  onClose: () => void;
  onAttach: (childId: string, alias?: string, descriptionOverride?: string) => void;
  excludeIds?: string[];
}

export const AttachAgentDialog = ({ open, onClose, onAttach, excludeIds = [] }: AttachAgentDialogProps) => {
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AgentDefinitionDto | null>(null);
  const [alias, setAlias] = useState('');
  const [descOverride, setDescOverride] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setAlias('');
    setDescOverride('');
    setLoading(true);
    agentService
      .getAgents({ pageSize: 200, isActive: true })
      .then((res) => {
        setAgents(res.items.filter((a) => !excludeIds.includes(a.id)));
      })
      .finally(() => setLoading(false));
    // excludeIds identity changes every render when callers pass a fresh array — join keeps the key stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, excludeIds.join(',')]);

  const agentOptions = useMemo(() => agents.map(agentToDropdownOption), [agents]);
  const agentSource = useStaticDropdownSource(agentOptions);

  const handleSubmit = () => {
    if (!selected) return;
    onAttach(selected.id, alias.trim() || undefined, descOverride.trim() || undefined);
    setSelected(null);
    setAlias('');
    setDescOverride('');
    onClose();
  };

  return (
    <CortexDialog open={open} onClose={onClose} title="Attach Sub-Agent" size="xl" data-testid="attach-agent-dialog">
      <CortexDialogBody>
        <div className="flex flex-col gap-4 pt-1">
          <FormField label="Agent" required description="Searchable picker — same foundation dropdown as Organization / Site.">
            <CoordioDropdown
              source={agentSource}
              value={selected?.id ?? null}
              onChange={(val) => setSelected(agents.find((a) => a.id === val) ?? null)}
              {...AGENT_DROPDOWN}
              clearable={false}
              disabled={loading}
              data-testid="attach-agent-select"
              data-pw="childAgentDefinitionId"
              data-field-type="select"
            />
            {loading && (
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">Loading agents…</p>
            )}
          </FormField>

          <FormField label="Alias" description="Optional tool-name override exposed to the orchestrator.">
            <Input
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder={selected ? `call_agent_${selected.slug}` : 'Optional alias'}
              data-testid="attach-agent-alias"
            />
          </FormField>

          <FormField label="Description override" description="Optional tool description shown to the orchestrator LLM.">
            <Textarea
              value={descOverride}
              onChange={(e) => setDescOverride(e.target.value)}
              placeholder={selected?.description ?? 'Optional description'}
              rows={5}
              className="min-h-[120px]"
              data-testid="attach-agent-description"
            />
          </FormField>
        </div>
      </CortexDialogBody>
      <CortexDialogFooter>
        <Button type="button" variant="outline" onClick={onClose} data-testid="attach-agent-cancel">
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={!selected} data-testid="attach-agent-confirm">
          Attach
        </Button>
      </CortexDialogFooter>
    </CortexDialog>
  );
};
